import { PlusOutlined } from '@ant-design/icons';
import { useLazyQuery } from '@apollo/client';
import { Divider, Empty, Form, Select, Upload } from 'antd';
import { debounce, forEach } from 'lodash';
import React, { useEffect, useState } from 'react';
import { LIMIT, NUMBER_FIELD_TYPES } from '../../../../../common/constants';
import {
  formValidatorRules,
  getBase64,
  validateImage,
} from '../../../../../common/utils';
import ModalComponent from '../../../../../components/ModalComponent';
import NumberComponent from '../../../../../components/NumberComponent';
import SelectComponent from '../../../../../components/SelectComponent';
import { FACILITATES_ADMIN } from '../../toy/graphql/Queries';

const { Item } = Form;
const { minAgeRule, maxAgeRule } = formValidatorRules;

const AgeGroupModal = ({
  isModalOpen,
  setIsModalOpen,
  onFinish,
  loadings,
  form,
  ageGroup,
  setAgeGroup,
  ageGroupImages,
  setAgeGroupImages,
  setVisible,
  setAgePreviewImage,
}) => {
  const [isValidateTrigger, setIsValidateTrigger] = useState(false);
  const [minAge, setMinAge] = useState();
  const [maxAge, setMaxAge] = useState();
  const initialFacilitateFilter = {
    skip: 0,
    limit: LIMIT,
    search: '',
  };

  const initialFacilitateSort = {
    field: 'name',
    order: 'ASC',
  };
  const { Option } = Select;

  const [facilitateList, setFacilitateList] = useState([]);
  const [facilitateLoading, setFacilitateLoading] = useState(false);
  const [facilitateSearch, setFacilitateSearch] = useState('');
  const [filterIsEnd, setFilterIsEnd] = useState(false);

  const [facilitatesAdmin, { loading }] = useLazyQuery(FACILITATES_ADMIN, {
    onCompleted: (response) => {
      if (response?.facilitatesAdmin?.data?.length < LIMIT) {
        setFilterIsEnd(true);
      }

      const optionsCopy = [];
      forEach(response?.facilitatesAdmin?.data, (item) =>
        optionsCopy?.push({
          label: item?.name?.replace(/\s/g, '\u00a0'),
          key: item?.id,
        }),
      );

      setFacilitateList([...facilitateList, ...optionsCopy]);
      setFacilitateLoading(false);
    },
    onError() {
      setFacilitateLoading(false);
    },
  });

  const handleFacilitateScroll = () => {
    const currentLength = facilitateList?.length;

    if (!filterIsEnd && !facilitateLoading) {
      setFacilitateLoading(true);

      facilitatesAdmin({
        variables: {
          filter: {
            limit: LIMIT,
            skip: currentLength,
            search: facilitateSearch,
          },
          sort: initialFacilitateSort,
        },
      });
    }
  };

  const handleFacilitateSearch = (searchTerm) => {
    const trimSearch = searchTerm?.trim();

    setFacilitateSearch(trimSearch);
    setFilterIsEnd(false);
    setFacilitateLoading(true);
    setFacilitateList([]);

    facilitatesAdmin({
      variables: {
        filter: {
          ...initialFacilitateFilter,
          limit: LIMIT,
          search: trimSearch,
        },
        sort: initialFacilitateSort,
      },
    });
  };
  const handleScrollDebounce = debounce(handleFacilitateScroll, 500);
  const handleSearchDebounce = debounce(handleFacilitateSearch, 500);

  useEffect(() => {
    handleScrollDebounce?.cancel();
  }, [handleScrollDebounce]);

  useEffect(() => {
    handleSearchDebounce?.cancel();
  }, [handleSearchDebounce]);

  const handleCancel = () => {
    setIsModalOpen(false);
    setAgeGroup();
    form?.resetFields();
    setMinAge();
    setMaxAge();
  };
  useEffect(() => {
    const optionsCopy = [];
    forEach(ageGroup?.facilitates, (item) =>
      optionsCopy?.push({
        label: item?.name?.replace(/\s/g, '\u00a0'),
        key: item?.id,
      }),
    );

    form?.setFieldsValue({
      minAge: ageGroup?.minAge,
      maxAge: ageGroup?.maxAge,
      facilitates: optionsCopy,
    });
  }, [ageGroup]);

  const handleImageChange = (info) => {
    validateImage(info, form, setAgeGroupImages, 'image');
  };

  const handlePreview = async (fileObj) => {
    let preview;
    if (!fileObj?.url && !fileObj?.preview) {
      preview = await getBase64(fileObj?.originFileObj);
    }
    setVisible(true);
    setAgePreviewImage(fileObj?.url || preview);
  };

  useEffect(() => {
    if (facilitateList?.length === 0) {
      setFacilitateLoading(true);
      facilitatesAdmin({
        variables: {
          filter: initialFacilitateFilter,
          sort: initialFacilitateSort,
        },
      });
    }
  }, []);

  return (
    <>
      <ModalComponent
        open={isModalOpen}
        onCancel={handleCancel}
        onOk={form?.submit}
        title={ageGroup ? 'Edit Age Group' : 'Add New Age Group'}
        okText="Save"
        confirmLoading={loadings}
        destroyOnClose
        forceRender
      >
        <Divider />
        <Form
          form={form}
          onFinish={(values) => {
            onFinish(values);
            setIsValidateTrigger(true);
          }}
          autoComplete="off"
          layout="vertical"
          validateTrigger={isValidateTrigger ? 'onChange' : 'onSubmit'}
          onFinishFailed={() => setIsValidateTrigger(true)}
        >
          <Item
            name="minAge"
            label="Min Age (in months)"
            rules={[
              { required: true, message: 'Please Enter Min Age!' },
              minAgeRule(minAge, maxAge),
            ]}
          >
            <NumberComponent
              allowClear
              placeholder="Enter Min Age"
              isFractional={false}
              type={NUMBER_FIELD_TYPES?.AGE}
              onChange={(e) => setMinAge(e?.target?.value)}
            />
          </Item>
          <Item
            name="maxAge"
            label="Max Age (in months)"
            rules={[
              { required: true, message: 'Please Enter Max Age!' },
              maxAgeRule(minAge, maxAge),
            ]}
          >
            <NumberComponent
              allowClear
              placeholder="Enter Max Age"
              isFractional={false}
              type={NUMBER_FIELD_TYPES?.AGE}
              onChange={(e) => setMaxAge(e?.target?.value)}
            />
          </Item>
          <Item
            name="facilitates"
            label="Select Facilitate"
            rules={[{ required: true, message: 'Please select facilitate!' }]}
          >
            <SelectComponent
              trigger="click"
              showSearch
              placeholder="Select Facilitate"
              mode="multiple"
              onPopupScroll={handleScrollDebounce}
              optionFilterProp="children"
              onSearch={handleSearchDebounce}
              loading={facilitateLoading}
              notFoundContent={
                !facilitateLoading && loading ? (
                  <span>Loading...</span>
                ) : (
                  <Empty image={Empty?.PRESENTED_IMAGE_SIMPLE} />
                )
              }
            >
              {facilitateList?.map((facilitate) => (
                <Option key={facilitate?.key} value={facilitate?.key}>
                  {facilitate?.label}
                </Option>
              ))}
            </SelectComponent>
          </Item>
          <Item
            label="Image"
            name="image"
            rules={[
              {
                validator: () => {
                  if (!ageGroupImages || ageGroupImages?.length === 0) {
                    return Promise.reject(new Error('Please Upload Image!'));
                  }
                  return Promise.resolve();
                },
              },
            ]}
          >
            <Upload
              listType="picture-card"
              maxCount={1}
              fileList={ageGroupImages}
              beforeUpload={() => false}
              onPreview={handlePreview}
              onChange={handleImageChange}
              showUploadList={{
                showDownloadIcon: false,
                showPreviewIcon: true,
                showRemoveIcon: true,
              }}
            >
              {ageGroupImages?.length !== 1 && <PlusOutlined />}
            </Upload>
          </Item>
        </Form>
      </ModalComponent>
    </>
  );
};

export default AgeGroupModal;
